/**
 * SearchInput — debounced, clearable search box (design-system-lld.md §4.7).
 * Absorbs `DebouncedSearchInput`'s debounce/clear behavior in a token-styled
 * control shared by pages and (eventually) DataTable. `DebouncedSearchInput`
 * itself is untouched for now — it remains DataTable's internal control
 * (fenced to the leads console per the LLD) and folds into this component in
 * a later phase.
 */
import * as React from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const DEFAULT_DEBOUNCE_MS = 300;

export interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  width?: string;
  className?: string;
  'aria-label'?: string;
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ value, onChange, placeholder = 'Search…', debounceMs = DEFAULT_DEBOUNCE_MS, width, className, ...props }, ref) => {
    const [draft, setDraft] = React.useState(value);
    const onChangeRef = React.useRef(onChange);
    onChangeRef.current = onChange;

    React.useEffect(() => {
      setDraft(value);
    }, [value]);

    React.useEffect(() => {
      if (draft === value) return undefined;
      const timer = setTimeout(() => onChangeRef.current(draft), debounceMs);
      return () => clearTimeout(timer);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft, debounceMs]);

    const handleClear = () => {
      setDraft('');
      onChangeRef.current('');
    };

    return (
      <div className={cn('relative', className)} style={width ? { width } : undefined}>
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
        <input
          ref={ref}
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={placeholder}
          aria-label={props['aria-label'] ?? placeholder}
          className="h-9 w-full rounded-lg border border-gray-300 bg-white py-1.5 pl-9 pr-8 text-sm text-gray-900 placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
        />
        {draft && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
            className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  },
);
SearchInput.displayName = 'SearchInput';
